import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarSync, Copy, Check, RefreshCw, PowerOff, Link2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const feedUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;
const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null);

const SetupSteps = () => (
  <div className="text-xs text-gray-600 space-y-1.5 bg-gray-50 rounded-md p-3 border" data-testid="calendar-sync-instructions">
    <p><strong>Google Calendar:</strong> Other calendars → + → From URL → paste the link → Add calendar.</p>
    <p><strong>Outlook:</strong> Add calendar → Subscribe from web → paste the link.</p>
    <p><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste the link.</p>
    <p className="text-gray-500">Your calendar app refreshes it automatically (Google usually every few hours). No password is shared.</p>
  </div>
);

export const CalendarSyncCard = () => {
  const [feed, setFeed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/calendar-feed`, { headers: authHeaders() })
      .then(async res => {
        const tz = browserTz();
        if (res.data.enabled && tz && res.data.timezone !== tz) {
          const upd = await axios.put(`${API}/calendar-feed/timezone`, { timezone: tz }, { headers: authHeaders() });
          setFeed(upd.data);
        } else {
          setFeed(res.data);
        }
      }).catch(() => setFeed({ enabled: false }));
  }, []);

  const run = async (method, msg) => {
    setBusy(true);
    try {
      const res = await axios({ method, url: `${API}/calendar-feed`, headers: authHeaders(),
        data: method === 'post' ? { timezone: browserTz() } : undefined });
      setFeed(res.data);
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl(feed.path));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the link and copy it manually');
    }
  };

  const confirmThen = (text, fn) => () => { if (window.confirm(text)) fn(); };

  return (
    <Card className="lg:col-span-3" data-testid="calendar-sync-card">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2"><CalendarSync className="h-5 w-5" /><span>Calendar Sync</span></CardTitle>
        <CardDescription>
          Show your shifts and time off in Google Calendar, Outlook or Apple Calendar using a private, read-only link.
          Please use this instead of tools that sign in with your password.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!feed ? <p className="text-sm text-gray-500">Loading…</p> : !feed.enabled ? (
          <Button onClick={() => run('post', 'Calendar link created')} disabled={busy} data-testid="calendar-sync-enable-button">
            <Link2 className="h-4 w-4 mr-2" /> Create my calendar link
          </Button>
        ) : (
          <>
            <div className="flex gap-2">
              <Input readOnly value={feedUrl(feed.path)} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid="calendar-sync-url" />
              <Button variant="outline" onClick={copy} data-testid="calendar-sync-copy-button">
                {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}{copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-gray-500" data-testid="calendar-sync-last-used">
              {feed.last_accessed_at ? `Last checked by your calendar app: ${fmt(feed.last_accessed_at)}` : 'Not used by a calendar app yet.'}
              {feed.timezone && <> Times use the {feed.timezone.replace(/_/g, ' ')} time zone.</>}
              {' '}Keep this link private — anyone with it can see your schedule.
            </p>
            <SetupSteps />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={busy} onClick={confirmThen('Create a new link? Calendars using the current link will stop updating until you add the new one.', () => run('post', 'New link created — the old link no longer works'))} data-testid="calendar-sync-reset-button">
                <RefreshCw className="h-4 w-4 mr-1" /> Reset link
              </Button>
              <Button variant="outline" size="sm" disabled={busy} className="text-red-600 hover:text-red-700" onClick={confirmThen('Turn off calendar sync? Your calendar app will stop receiving schedule updates.', () => run('delete', 'Calendar sync turned off'))} data-testid="calendar-sync-disable-button">
                <PowerOff className="h-4 w-4 mr-1" /> Turn off
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Admin view inside the user edit dialog: shows whether a calendar link exists and lets admins turn it off.
export const AdminCalendarFeedStatus = ({ userId }) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/users/${userId}/calendar-feed`, { headers: authHeaders() })
      .then(res => setInfo(res.data)).catch(() => setInfo(null));
  }, [userId]);

  if (!info) return null;
  const revoke = async () => {
    try {
      await axios.delete(`${API}/users/${userId}/calendar-feed`, { headers: authHeaders() });
      setInfo({ enabled: false });
      toast.success('Calendar sync link turned off');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to turn off calendar link');
    }
  };
  return (
    <div className="rounded-lg border p-3 flex items-center gap-3 text-xs" data-testid="admin-calendar-feed-status">
      <CalendarSync className="h-4 w-4 text-gray-500 flex-shrink-0" />
      <div className="flex-1 text-gray-700">
        <strong>Calendar sync link:</strong>{' '}
        {info.enabled
          ? `On${info.last_accessed_at ? ` — last checked ${fmt(info.last_accessed_at)}` : ' — not used yet'}`
          : 'Off'}
      </div>
      {info.enabled && (
        <Button type="button" size="sm" variant="outline" className="h-7 text-red-600" onClick={revoke} data-testid="admin-calendar-feed-revoke-button">
          Turn off
        </Button>
      )}
    </div>
  );
};
